import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import LabeledIncomeStatus from 'components/molecules/labeledStatus/LabaleIncomeStatus';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import { useTranslation } from 'react-i18next';
import {
  PiHandCoinsBold,
  PiReceiptFill,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { INCOME_TYPE } from 'services/cash-flow/income/types';
import { useGetEventCosts } from 'services/events/cost';
import { useGetEventIncomes } from 'services/events/income';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { getNodes } from 'utils/table/nodes.utils';

interface IEventFinanceItem {
  id: string;
  name: string;
  type: 'income' | 'cost';
  amountGross: number;
  amountNet: number;
  tax: number;
}

const EventBalance = () => {
  const eventId = useParamId('event');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const eventIncomes = useGetEventIncomes(eventId, true);
  const eventCosts = useGetEventCosts(eventId, true);

  const combinedData: IEventFinanceItem[] = [
    ...(eventIncomes.data || []).map(income => ({
      id: income.id,
      name: income.incomeName,
      type: 'income' as const,
      amountGross: income.amount.grossAmount,
      amountNet: income.amount.netAmount,
      tax: income.tax,
    })),
    ...(eventCosts.data || []).map(cost => ({
      id: cost.id,
      name: cost.costName,
      type: 'cost' as const,
      amountGross: cost.amount.grossAmount,
      amountNet: cost.amount.netAmount,
      tax: cost.tax,
    })),
  ];

  const tableData = getNodes<IEventFinanceItem>(combinedData);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const goCreateCost = () => navigate(`/event/${eventId}/cost/create`);

  const goCreateIncome = () => navigate(`/event/${eventId}/income/create`);

  const onSelectChange = (action: Action, state: State) => {
    const rowData = tableData.nodes || [];

    const selectedRow = rowData.find(row => row.id === action.payload.id);

    if (selectedRow?.type === 'cost') {
      navigate(`/event/${eventId}/cost/${selectedRow?.id}`);
    } else {
      navigate(`/event/${eventId}/income/${selectedRow?.id}`);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const amountNetLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amountNet
  );
  const amountGrossLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amountGross
  );
  const taxLength = getMaxFormattedLength(tableData?.nodes ?? [], e => e?.tax);

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <>
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={handleComponentVisible}
                outsideChildren={
                  <DropdownMenu
                    ref={ref}
                    visible={isComponentVisible}
                    style={{ padding: 0 }}
                    justifyPosition='end'>
                    <Button isClear onClick={goCreateCost} fullWidth>
                      <div
                        style={{
                          alignContent: 'baseline',
                          display: 'flex',
                          width: '100%',
                          gap: '12px',
                        }}>
                        <Button.Icon icon={<PiReceiptFill />} />
                        <Button.Text text={t('event.tabs.cost')} />
                      </div>
                    </Button>

                    <Button isClear onClick={goCreateIncome}>
                      <Button.Icon icon={<PiHandCoinsBold />} />
                      <Button.Text text={t('event.tabs.income')} />
                    </Button>
                  </DropdownMenu>
                }>
                <Button.Text text={t('button.add')} />
              </Button>
            </>
          }
        />
      }>
      <Table
        id={'EventBalance'}
        size='compact'
        data={tableData}
        select={select}
        columns={[
          {
            label: t('input.event.name'),
            renderCell: ({ name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.event.net'),
            renderCell: ({ amountNet }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountNet, {
                    length: amountNetLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.event.gross'),
            renderCell: ({ amountGross }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountGross, {
                    length: amountGrossLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.event.tax'),
            renderCell: ({ tax }) => (
              <TableItem>
                <TableItem.Currency
                  text={`${formatCurrency(tax, {
                    length: taxLength,
                    suffix: '%',
                  })} `}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.event.type'),
            renderCell: ({ type }) => (
              <TableItem>
                <LabeledIncomeStatus
                  status={
                    type === 'income'
                      ? INCOME_TYPE.basic
                      : INCOME_TYPE.cost
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default EventBalance; 